import { useState, useRef, useEffect, useCallback } from "react";
import { api } from "../api/client";
import { useAI } from "./AIPanel";

interface ChatMsg {
  role: "user" | "assistant";
  content: string;
}

const SUGGESTIONS = [
  "帮我写一个高级前端工程师的JD",
  "如何提高简历筛选的通过率？",
  "面试官应该注意哪些评估维度？",
  "背调一般核查哪些项目？",
  "试用期转正考核怎么做？",
];

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { open: openAIPanel } = useAI();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    const next = [...messages, { role: "user" as const, content: trimmed }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const result = await api.aiChat(
        next.map((m) => ({ role: m.role, content: m.content }))
      );
      setMessages((prev) => [...prev, { role: "assistant", content: result }]);
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "**请求失败**\n\n" + (e?.message || String(e)),
        },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [messages, loading]);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const clearChat = () => setMessages([]);

  return (
    <>
      {/* Floating button */}
      <button
        className="ai-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label="AI 助手"
        title="AI 助手"
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z" />
            <path d="M9 21h6" />
          </svg>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="ai-assistant">
          <div className="ai-assistant-head">
            <div className="ai-assistant-title">
              <span className="ai-badge">AI</span>
              <span>招聘智能助手</span>
              <span className="ai-assistant-model">qwen3.7-plus</span>
            </div>
            <div className="ai-assistant-actions">
              {messages.length > 0 && (
                <button className="ai-assistant-btn" onClick={clearChat} title="清空对话">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              )}
              <button className="ai-assistant-btn" onClick={() => setOpen(false)} title="关闭">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="ai-assistant-body" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="ai-assistant-welcome">
                <div className="ai-assistant-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                    <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z" />
                    <path d="M9 21h6" />
                  </svg>
                </div>
                <p className="ai-assistant-greeting">你好，我是招聘智能助手</p>
                <p className="ai-assistant-sub">可以帮你写JD、分析简历、设计面试方案、解读招聘数据</p>
                <div className="ai-assistant-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      className="ai-suggestion-chip"
                      onClick={() => send(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={"ai-chat-msg ai-chat-msg-" + m.role}>
                <div className="ai-chat-avatar">
                  {m.role === "user" ? "我" : "AI"}
                </div>
                <div className="ai-chat-bubble">
                  {m.role === "assistant" ? (
                    <Markdown text={m.content} />
                  ) : (
                    m.content
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="ai-chat-msg ai-chat-msg-assistant">
                <div className="ai-chat-avatar">AI</div>
                <div className="ai-chat-bubble ai-chat-typing">
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                </div>
              </div>
            )}
          </div>

          <div className="ai-assistant-input">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="输入招聘相关问题…（Enter 发送，Shift+Enter 换行）"
              rows={2}
              disabled={loading}
            />
            <button
              className="ai-assistant-send"
              onClick={() => send(input)}
              disabled={loading || !input.trim()}
              aria-label="发送"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Simple inline markdown renderer (reused from AIPanel pattern)
function Markdown({ text }: { text: string }) {
  const lines = text.split("\n");
  const out: any[] = [];
  let list: any[] = [];
  let key = 0;
  const flush = () => {
    if (list.length > 0) {
      out.push(<ul key={"ul" + key++} className="ai-md-list">{list}</ul>);
      list = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); continue; }
    if (line.startsWith("### ")) { flush(); out.push(<h4 key={key++} className="ai-md-h3">{line.slice(4)}</h4>); continue; }
    if (line.startsWith("## ")) { flush(); out.push(<h3 key={key++} className="ai-md-h2">{line.slice(3)}</h3>); continue; }
    if (line.startsWith("# ")) { flush(); out.push(<h2 key={key++} className="ai-md-h1">{line.slice(2)}</h2>); continue; }
    const num = line.match(/^(\d+)\.\s+(.*)/);
    if (num) { list.push(<li key={key++} className="ai-md-li">{renderInline(num[2])}</li>); continue; }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      list.push(<li key={key++} className="ai-md-li">{renderInline(line.slice(2))}</li>);
      continue;
    }
    flush();
    out.push(<p key={key++} className="ai-md-p">{renderInline(line)}</p>);
  }
  flush();
  return <div className="ai-md">{out}</div>;
}

function renderInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}
